import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  INSP_STATUS, RESULT_STYLE, MEASURE_STATUS, MEASURE_STATUS_OPTIONS, DEFECT_RESULTS, kindLabel, resultLabel, canInsp,
} from "@/lib/inspectionConstants";
import {
  ClipboardCheck, AlarmClock, CalendarClock, Plus, Search, Pencil, Trash2, Copy, CheckCircle2,
  Power, PackageSearch, Tags, Archive, Loader2, Wrench, CalendarDays, Settings, FileText, AlertTriangle, History, RotateCw, BarChart3, Camera, Image as ImageIcon,
} from "lucide-react";
import PageHelp from "@/components/PageHelp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import InspectionForm from "@/components/inspections/InspectionForm";
import AssetForm from "@/components/inspections/AssetForm";
import CompleteDialog from "@/components/inspections/CompleteDialog";
import InspectionCalendar from "@/components/inspections/InspectionCalendar";
import DocumentsDialog from "@/components/inspections/DocumentsDialog";
import MeasureForm from "@/components/inspections/MeasureForm";
import AssetHistoryDialog from "@/components/inspections/AssetHistoryDialog";
import InspectionReports from "@/components/inspections/InspectionReports";
import MeasurePhotosDialog from "@/components/inspections/MeasurePhotosDialog";
import ImportInspectionsDialog from "@/components/inspections/ImportInspectionsDialog";
import CopyInspectionsDialog from "@/components/inspections/CopyInspectionsDialog";
import { FileSpreadsheet } from "lucide-react";

function StatusBadge({ s }) {
  const st = INSP_STATUS[s] || INSP_STATUS.geplant;
  return <Badge variant="outline" className={`${st.bg} ${st.text} ${st.border} text-[11px]`} data-testid={`status-${s}`}>{st.label}</Badge>;
}

export default function Inspections() {
  const { user } = useAuth();
  const role = user?.role || "user";
  const canWrite = canInsp(user, "write");
  const canDelete = canInsp(user, "delete");
  const isAdmin = role === "admin";
  const canComplete = canInsp(user, "complete");
  const canSettings = canInsp(user, "settings");
  const [params, setParams] = useSearchParams();

  const [tab, setTab] = useState(params.get("tab") || "list");
  const [items, setItems] = useState(null);
  const [assets, setAssets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [projects, setProjects] = useState([]);
  const [records, setRecords] = useState(null);
  const [calItems, setCalItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState(params.get("scope") || "all");
  const [catFilter, setCatFilter] = useState("");
  const [projFilter, setProjFilter] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [assetOpen, setAssetOpen] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [completeItem, setCompleteItem] = useState(null);
  const [docItem, setDocItem] = useState(null);
  const [delItem, setDelItem] = useState(null);
  const [measures, setMeasures] = useState(null);
  const [measureScope, setMeasureScope] = useState("open");
  const [measureItem, setMeasureItem] = useState(null);
  const [measureOpen, setMeasureOpen] = useState(false);
  const [measurePrefill, setMeasurePrefill] = useState(null);
  const [historyAsset, setHistoryAsset] = useState(null);
  const [photoMeasure, setPhotoMeasure] = useState(null);
  const [newCat, setNewCat] = useState("");

  const loadCats = useCallback(() => api.get("/inspections/categories").then((r) => setCategories(r.data)).catch(() => {}), []);
  const loadAssets = useCallback(() => api.get("/inspections/assets").then((r) => setAssets(r.data)).catch(() => {}), []);
  const loadProjects = useCallback(() => api.get("/inspections/projects").then((r) => setProjects(r.data)).catch(() => {}), []);
  const loadList = useCallback(() => {
    const p = new URLSearchParams();
    if (scope !== "all") p.set("scope", scope);
    if (q) p.set("q", q);
    if (catFilter) p.set("category", catFilter);
    if (projFilter) p.set("project", projFilter);
    api.get(`/inspections?${p.toString()}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [scope, q, catFilter, projFilter]);
  const loadRecords = useCallback(() => api.get("/inspections/records").then((r) => setRecords(r.data)).catch(() => {}), []);
  const loadCalendar = useCallback(() => {
    api.get("/inspections?scope=all").then((r) => setCalItems(r.data)).catch(() => {});
    api.get("/inspections/records").then((r) => setRecords(r.data)).catch(() => {});
  }, []);
  const loadSettings = useCallback(() => api.get("/inspections/settings").then((r) => setSettings(r.data)).catch(() => {}), []);
  const loadMeasures = useCallback(() => api.get(`/inspections/measures?scope=${measureScope}`).then((r) => setMeasures(r.data)).catch(() => {}), [measureScope]);

  useEffect(() => { loadCats(); loadAssets(); loadProjects(); }, [loadCats, loadAssets, loadProjects]);
  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (tab === "archive") loadRecords(); }, [tab, loadRecords]);
  useEffect(() => { if (tab === "calendar") loadCalendar(); }, [tab, loadCalendar]);
  useEffect(() => { if (tab === "settings") loadSettings(); }, [tab, loadSettings]);
  useEffect(() => { if (tab === "measures") loadMeasures(); }, [tab, loadMeasures]);
  useEffect(() => { const sc = params.get("scope"); if (sc) { setScope(sc); setTab("list"); } }, [params]);

  const saveSettings = async () => {
    try { await api.put("/inspections/settings", settings); toast.success("Einstellungen gespeichert"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); loadList(); loadAssets(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const seedCats = () => act(() => api.post("/inspections/categories/seed-defaults"), "Beispielkategorien hinzugefügt").then(loadCats);
  const addCat = async () => { if (!newCat.trim()) return; try { await api.post("/inspections/categories", { name: newCat }); setNewCat(""); loadCats(); toast.success("Kategorie hinzugefügt"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const SCOPES = [
    { value: "all", label: "Alle" }, { value: "open", label: "Offen" }, { value: "overdue", label: "Überfällig" },
    { value: "soon", label: "Bald fällig" }, { value: "inactive", label: "Deaktiviert" },
  ];

  return (
    <div className="space-y-6 fade-up" data-testid="inspections-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-head font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <ClipboardCheck className="h-8 w-8 text-primary" />Prüfungen & Wartungen
            <PageHelp id="inspections" title="Prüfungen & Wartungen" text="Zentrale Verwaltung aller wiederkehrenden Prüfungen und Wartungen Ihrer Einrichtung: Termine, Intervalle, Anlagen, Abschlüsse und Archiv. Überfällige Vorgänge werden rot hervorgehoben." />
          </h1>
          <p className="text-slate-500 mt-1">Wiederkehrende Prüfungen, Fristen, Anlagen und Wartungsarchiv.</p>
        </div>
        {canWrite && (
          <div className="flex items-center gap-2">
            <Button variant="outline" className="h-11" onClick={() => setImportOpen(true)} data-testid="import-inspections-btn">
              <FileSpreadsheet className="h-4 w-4 mr-2" />Prüfungen aus Excel importieren
            </Button>
            <Button variant="outline" className="h-11" onClick={() => setCopyOpen(true)} data-testid="copy-inspections-btn">
              <Copy className="h-4 w-4 mr-2" />Prüfungen kopieren
            </Button>
            <Button className="h-11" onClick={() => { setEditItem(null); setFormOpen(true); }} data-testid="new-inspection-btn">
              <Plus className="h-4 w-4 mr-2" />Neue Prüfung / Wartung
            </Button>
          </div>
        )}
      </div>
      <ImportInspectionsDialog open={importOpen} onOpenChange={setImportOpen} onImported={loadList} />
      <CopyInspectionsDialog open={copyOpen} onOpenChange={setCopyOpen} onCopied={loadList} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="insp-tabs">
          <TabsTrigger value="calendar" data-testid="tab-calendar"><CalendarDays className="h-4 w-4 mr-1.5" />Kalender</TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list"><ClipboardCheck className="h-4 w-4 mr-1.5" />Prüfungen</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets"><PackageSearch className="h-4 w-4 mr-1.5" />Anlagen</TabsTrigger>
          <TabsTrigger value="measures" data-testid="tab-measures"><AlertTriangle className="h-4 w-4 mr-1.5" />Mängel</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories"><Tags className="h-4 w-4 mr-1.5" />Kategorien</TabsTrigger>
          <TabsTrigger value="archive" data-testid="tab-archive"><Archive className="h-4 w-4 mr-1.5" />Archiv</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports"><BarChart3 className="h-4 w-4 mr-1.5" />Auswertungen</TabsTrigger>
          {canSettings && <TabsTrigger value="settings" data-testid="tab-settings"><Settings className="h-4 w-4 mr-1.5" />Einstellungen</TabsTrigger>}
        </TabsList>

        {/* ---------------- Calendar ---------------- */}
        <TabsContent value="calendar" className="mt-4">
          <InspectionCalendar inspections={calItems} records={records || []} onEventClick={(e) => { if (e.type === "due") { setEditItem(e.ref); setFormOpen(true); } }} />
        </TabsContent>

        {/* ---------------- Inspections list ---------------- */}
        <TabsContent value="list" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen…" className="pl-9" data-testid="insp-search" />
            </div>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-40" data-testid="insp-scope"><SelectValue /></SelectTrigger>
              <SelectContent>{SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={catFilter || "__all"} onValueChange={(v) => setCatFilter(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="insp-cat-filter"><SelectValue placeholder="Kategorie" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Alle Kategorien</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={projFilter || "__all"} onValueChange={(v) => setProjFilter(v === "__all" ? "" : v)}>
              <SelectTrigger className="w-48" data-testid="insp-proj-filter"><SelectValue placeholder="Projekt" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">Alle Projekte</SelectItem>
                <SelectItem value="__none">Ohne Projektzuordnung</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {!items ? <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            : items.length === 0 ? <Card className="p-8 text-center text-slate-400" data-testid="insp-empty">Keine Prüfungen gefunden.</Card>
            : (
              <div className="space-y-2">
                {items.map((it) => (
                  <Card key={it.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`insp-row-${it.id}`}>
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: (INSP_STATUS[it.display_status] || {}).dot }} />
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-semibold text-slate-900 flex items-center gap-2">
                        {it.is_maintenance ? <Wrench className="h-3.5 w-3.5 text-slate-400" /> : null}{it.title}
                        {it.project_name && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200" data-testid={`insp-proj-badge-${it.id}`}>{it.project_name}</Badge>}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {kindLabel(it.kind)}{it.category ? ` · ${it.category}` : ""}{it.building ? ` · ${it.building}` : ""}{it.room ? ` · ${it.room}` : ""}
                        {it.asset_names?.length ? ` · ${it.asset_names.join(", ")}` : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-400">Fällig</div>
                      <div className={`text-sm font-mono-tech ${it.display_status === "ueberfaellig" ? "text-red-600 font-bold" : "text-slate-700"}`}>
                        {it.next_due || "—"}{it.overdue_days > 0 ? ` (+${it.overdue_days}T)` : ""}
                      </div>
                    </div>
                    <StatusBadge s={it.display_status} />
                    <div className="flex items-center gap-1">
                      <button onClick={() => setDocItem(it)} title={`Dokumente${it.doc_count ? ` (${it.doc_count})` : ""}`} data-testid={`docs-btn-${it.id}`} className="relative p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-primary">
                        <FileText className={`h-4 w-4 ${it.doc_count ? "text-primary" : ""}`} />
                        {it.doc_count > 0 && <span data-testid={`doc-count-${it.id}`} className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-primary text-white text-[9px] font-bold flex items-center justify-center">{it.doc_count}</span>}
                      </button>
                      {it.photo_count > 0 && (
                        <button onClick={() => setDocItem(it)} title={`Fotos (${it.photo_count})`} data-testid={`photo-btn-${it.id}`} className="relative p-2 rounded-lg hover:bg-slate-100 text-emerald-500 hover:text-emerald-600">
                          <ImageIcon className="h-4 w-4" />
                          <span data-testid={`photo-count-${it.id}`} className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-emerald-500 text-white text-[9px] font-bold flex items-center justify-center">{it.photo_count}</span>
                        </button>
                      )}
                      {canComplete && it.active !== false && (
                        <button onClick={() => setCompleteItem(it)} title="Abschließen" data-testid={`complete-btn-${it.id}`} className="p-2 rounded-lg hover:bg-green-50 text-slate-400 hover:text-green-600"><CheckCircle2 className="h-4 w-4" /></button>
                      )}
                      {canWrite && <button onClick={() => { setEditItem(it); setFormOpen(true); }} title="Bearbeiten" data-testid={`edit-btn-${it.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>}
                      {canWrite && <button onClick={() => act(() => api.post(`/inspections/${it.id}/duplicate`), "Dupliziert")} title="Duplizieren" data-testid={`dup-btn-${it.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700"><Copy className="h-4 w-4" /></button>}
                      {isAdmin && <button onClick={() => act(() => api.post(`/inspections/${it.id}/${it.active === false ? "activate" : "deactivate"}`), it.active === false ? "Aktiviert" : "Deaktiviert")} title={it.active === false ? "Aktivieren" : "Deaktivieren"} data-testid={`toggle-btn-${it.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-amber-600"><Power className="h-4 w-4" /></button>}
                      {canDelete && <button onClick={() => setDelItem(it)} title="Löschen" data-testid={`del-btn-${it.id}`} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </Card>
                ))}
              </div>
            )}
        </TabsContent>

        {/* ---------------- Measures (Mängel) ---------------- */}
        <TabsContent value="measures" className="space-y-4 mt-4">
          <div className="flex flex-wrap items-center gap-2">
            {canWrite && <Button onClick={() => { setMeasureItem(null); setMeasurePrefill(null); setMeasureOpen(true); }} data-testid="new-measure-btn"><Plus className="h-4 w-4 mr-2" />Neuer Mangel / Maßnahme</Button>}
            <Select value={measureScope} onValueChange={setMeasureScope}>
              <SelectTrigger className="w-40" data-testid="measure-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Offen</SelectItem>
                <SelectItem value="overdue">Überfällig</SelectItem>
                <SelectItem value="all">Alle</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!measures ? <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            : measures.length === 0 ? <Card className="p-8 text-center text-slate-400">Keine Mängel / Maßnahmen.</Card>
            : (
              <div className="space-y-2">
                {measures.map((m) => { const st = MEASURE_STATUS[m.status] || {}; return (
                  <Card key={m.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`measure-row-${m.id}`}>
                    <AlertTriangle className={`h-4 w-4 shrink-0 ${m.is_overdue ? "text-red-600" : "text-orange-500"}`} />
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-semibold text-slate-900">{m.title}</div>
                      <div className="text-xs text-slate-500 truncate">{m.asset_label || (m.asset_names || []).join(", ")}{m.responsible ? ` · ${m.responsible}` : ""}{m.required_action ? ` · ${m.required_action}` : ""}</div>
                    </div>
                    {m.due_date && <div className="text-right"><div className="text-xs text-slate-400">Frist</div><div className={`text-sm font-mono-tech ${m.is_overdue ? "text-red-600 font-bold" : "text-slate-700"}`}>{m.due_date}</div></div>}
                    <Select value={m.status} onValueChange={(v) => act(() => api.put(`/inspections/measures/${m.id}`, { ...m, status: v }), "Status aktualisiert").then(loadMeasures)} disabled={!canWrite}>
                      <SelectTrigger className="w-48 h-9" data-testid={`measure-status-${m.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{MEASURE_STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setPhotoMeasure(m)} title="Nachweisfotos" data-testid={`measure-photos-${m.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-primary"><Camera className="h-4 w-4" /></button>
                      {canWrite && <button onClick={() => act(() => api.post(`/inspections/measures/${m.id}/create-followup`), "Nachprüfung angelegt").then(loadMeasures)} title="Nachprüfung anlegen" data-testid={`measure-followup-${m.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-primary"><RotateCw className="h-4 w-4" /></button>}
                      {canWrite && <button onClick={() => { setMeasureItem(m); setMeasurePrefill(null); setMeasureOpen(true); }} title="Bearbeiten" data-testid={`measure-edit-${m.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>}
                      {canDelete && <button onClick={() => act(() => api.delete(`/inspections/measures/${m.id}`), "Gelöscht").then(loadMeasures)} title="Löschen" data-testid={`measure-del-${m.id}`} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </Card>
                ); })}
              </div>
            )}
        </TabsContent>

        {/* ---------------- Assets ---------------- */}
        <TabsContent value="assets" className="space-y-4 mt-4">
          {canWrite && <Button onClick={() => { setEditAsset(null); setAssetOpen(true); }} data-testid="new-asset-btn"><Plus className="h-4 w-4 mr-2" />Neue Anlage / Gerät</Button>}
          {assets.length === 0 ? <Card className="p-8 text-center text-slate-400">Noch keine Anlagen angelegt.</Card>
            : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {assets.map((a) => (
                  <Card key={a.id} className="p-4" data-testid={`asset-card-${a.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{a.name}</div>
                        <div className="text-xs text-slate-500">{a.category || "—"}</div>
                      </div>
                      {canWrite && <button onClick={() => { setEditAsset(a); setAssetOpen(true); }} data-testid={`edit-asset-${a.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-400"><Pencil className="h-3.5 w-3.5" /></button>}
                    </div>
                    <div className="text-xs text-slate-500 mt-2 space-y-0.5">
                      {(a.building || a.room) && <div>{[a.building, a.floor, a.room].filter(Boolean).join(" · ")}</div>}
                      {a.inventory_number && <div>Inv.-Nr.: {a.inventory_number}</div>}
                      <div className="text-primary">{a.inspection_count || 0} Prüfung(en)</div>
                    </div>
                    <button onClick={() => setHistoryAsset(a)} data-testid={`asset-history-${a.id}`} className="mt-2 text-xs text-primary flex items-center gap-1 hover:underline"><History className="h-3 w-3" />Historie (5 Jahre)</button>
                  </Card>
                ))}
              </div>
            )}
        </TabsContent>

        {/* ---------------- Categories ---------------- */}
        <TabsContent value="categories" className="space-y-4 mt-4">
          {canSettings ? (
            <>
              <div className="flex flex-wrap gap-2">
                <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="Neue Kategorie…" className="max-w-xs" data-testid="new-cat-input" onKeyDown={(e) => e.key === "Enter" && addCat()} />
                <Button onClick={addCat} data-testid="add-cat-btn"><Plus className="h-4 w-4 mr-1" />Hinzufügen</Button>
                <Button variant="outline" onClick={seedCats} data-testid="seed-cats-btn">Beispielkategorien hinzufügen</Button>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {categories.map((c) => (
                  <Card key={c.id} className="p-3 flex items-center justify-between" data-testid={`cat-${c.id}`}>
                    <span className="text-sm text-slate-800">{c.name}</span>
                    <button onClick={() => act(() => api.delete(`/inspections/categories/${c.id}`), "Kategorie gelöscht").then(loadCats)} data-testid={`del-cat-${c.id}`} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </Card>
                ))}
              </div>
            </>
          ) : <Card className="p-8 text-center text-slate-400">Nur Administrator / Einrichtungsleitung dürfen Kategorien verwalten.</Card>}
        </TabsContent>

        {/* ---------------- Archive ---------------- */}
        <TabsContent value="archive" className="space-y-3 mt-4">
          {!records ? <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
            : records.length === 0 ? <Card className="p-8 text-center text-slate-400">Noch keine abgeschlossenen Vorgänge.</Card>
            : records.map((r) => (
              <Card key={r.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`record-${r.id}`}>
                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                <div className="flex-1 min-w-[160px]">
                  <div className="font-semibold text-slate-900">{r.title}</div>
                  <div className="text-xs text-slate-500">{kindLabel(r.kind)}{r.category ? ` · ${r.category}` : ""}{r.performed_by ? ` · ${r.performed_by}` : ""}{r.asset_names?.length ? ` · ${r.asset_names.join(", ")}` : ""}</div>
                </div>
                <div className="text-right"><div className="text-xs text-slate-400">Durchgeführt</div><div className="text-sm font-mono-tech text-slate-700">{r.actual_date}</div></div>
                <Badge variant="outline" className={`${RESULT_STYLE[r.result] || ""} text-[11px]`}>{resultLabel(r.result)}</Badge>
                {r.next_due && <span className="text-xs text-slate-400">Folgetermin: {r.next_due}</span>}
                {canWrite && DEFECT_RESULTS.has(r.result) && (
                  <button onClick={() => { setMeasureItem(null); setMeasurePrefill({ title: `Mangel: ${r.title}`, description: r.defects_found || "", inspection_id: r.inspection_id, record_id: r.id, asset_ids: r.asset_ids || [], asset_label: (r.asset_names || []).join(", ") || r.building || "" }); setMeasureOpen(true); }} data-testid={`record-measure-${r.id}`} className="text-xs text-red-600 flex items-center gap-1 hover:underline"><AlertTriangle className="h-3 w-3" />Maßnahme erstellen</button>
                )}
              </Card>
            ))}
        </TabsContent>
        {/* ---------------- Reports / Export ---------------- */}
        <TabsContent value="reports" className="mt-4">
          <InspectionReports />
        </TabsContent>

        {/* ---------------- Settings / Reminders ---------------- */}
        <TabsContent value="settings" className="mt-4">
          {canSettings && settings ? (
            <Card className="p-5 max-w-xl space-y-4" data-testid="settings-card">
              <h3 className="font-head font-bold text-slate-900 flex items-center gap-2"><AlarmClock className="h-4 w-4 text-primary" />Erinnerungen</h3>
              <div>
                <Label className="text-xs">Erinnerung vor Fälligkeit (Tage)</Label>
                <div className="flex gap-4 mt-2">
                  {[90, 30, 14, 7].map((d) => (
                    <label key={d} className="flex items-center gap-1.5 text-sm cursor-pointer">
                      <Checkbox checked={settings.lead_times.includes(d)} data-testid={`lead-${d}`}
                        onCheckedChange={(v) => setSettings((s) => ({ ...s, lead_times: v ? [...s.lead_times, d].sort((a, b) => b - a) : s.lead_times.filter((x) => x !== d) }))} />{d}
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Switch checked={settings.notify_due_day} onCheckedChange={(v) => setSettings((s) => ({ ...s, notify_due_day: v }))} data-testid="notify-due" />Am Fälligkeitstag erinnern</label>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Switch checked={settings.notify_overdue} onCheckedChange={(v) => setSettings((s) => ({ ...s, notify_overdue: v }))} data-testid="notify-overdue" />Bei Überschreitung erinnern</label>
              <label className="flex items-center gap-2 text-sm cursor-pointer"><Switch checked={settings.email_enabled} onCheckedChange={(v) => setSettings((s) => ({ ...s, email_enabled: v }))} data-testid="email-enabled" />E-Mail-Erinnerungen senden (täglich, 06:00)</label>
              <div>
                <Label className="text-xs">Zusätzliche E-Mail-Empfänger (kommagetrennt)</Label>
                <Input value={(settings.email_recipients || []).join(", ")} data-testid="email-recipients" className="mt-1" placeholder="name@einrichtung.de"
                  onChange={(e) => setSettings((s) => ({ ...s, email_recipients: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) }))} />
                <p className="text-[11px] text-slate-400 mt-1">Administratoren des Arbeitsbereichs erhalten die Erinnerungen automatisch.</p>
              </div>
              <Button onClick={saveSettings} data-testid="save-settings-btn">Einstellungen speichern</Button>
            </Card>
          ) : <Card className="p-8 text-center text-slate-400">Nur Administrator / Einrichtungsleitung dürfen Erinnerungen konfigurieren.</Card>}
        </TabsContent>
      </Tabs>
      <InspectionForm open={formOpen} onClose={() => setFormOpen(false)} onSaved={() => { loadList(); loadCalendar(); loadProjects(); }} initial={editItem} categories={categories} assets={assets} projects={projects} isAdmin={isAdmin} />
      <AssetForm open={assetOpen} onClose={() => setAssetOpen(false)} onSaved={loadAssets} initial={editAsset} categories={categories} />
      <CompleteDialog open={!!completeItem} onClose={() => setCompleteItem(null)} onDone={() => { loadList(); loadRecords(); if (tab === "calendar") loadCalendar(); }} inspection={completeItem} />
      <DocumentsDialog open={!!docItem} onClose={() => setDocItem(null)} inspection={docItem} canWrite={canWrite} canDelete={canDelete} />
      <MeasureForm open={measureOpen} onClose={() => setMeasureOpen(false)} onSaved={() => { loadMeasures(); loadList(); }} initial={measureItem} prefill={measurePrefill} />
      <AssetHistoryDialog open={!!historyAsset} onClose={() => setHistoryAsset(null)} asset={historyAsset} />
      <MeasurePhotosDialog open={!!photoMeasure} onClose={() => setPhotoMeasure(null)} measure={photoMeasure} canWrite={canWrite} />

      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Prüfung löschen?</AlertDialogTitle>
            <AlertDialogDescription>Die Prüfung <b>{delItem?.title}</b> wird entfernt. Bereits archivierte Abschlüsse und deren Unterlagen bleiben erhalten.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => act(() => api.delete(`/inspections/${delItem.id}`), "Gelöscht").then(() => setDelItem(null))} className="bg-red-600 hover:bg-red-700" data-testid="confirm-del-inspection">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
